import { Settings } from "lucide-react";
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { Service } from '../../../shared/hooks/useServiceSelection';
import ImageWithPlaceholder from "../../../shared/components/ImageWithPlaceholderProps";
import { SectionTitle } from "./SectionTitle";

interface ServicesSectionProps {
  services: Service[];
  currency: any;
  colors?: any;
  Icon?: React.ComponentType<any> | string;
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  services,
  currency,
  colors = {},
  Icon = Settings
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const defaultColors = {
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    cardShadow: "rgba(221,184,137,0.07)",
    cardShadowSelected: "rgba(221,184,137,0.1)",
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    iconColor: "#DDB889"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  // Create style objects from the colors
  const cardStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    boxShadow: `0 0 15px ${mergedColors.cardShadow}`
  };

  const cardHoverStyle = {
    boxShadow: `0 0 20px ${mergedColors.cardShadowSelected}`
  };

  const textStyle = {
    color: `${mergedColors.subtitleText}`
  };

  const iconStyle = {
    color: mergedColors.iconColor
  };

  const imageBorderStyle = {
    borderColor: mergedColors.cardBorder
  };

  const imageShadowStyle = {
    boxShadow: `0 0 10px ${mergedColors.cardShadow}`
  };

  // Custom placeholder colors based on the theme
  const placeholderColors = {
    from: mergedColors.cardBackground,
    to: mergedColors.cardBackground,
    iconBackground: 'transparent'
  };

  return (
    <div
      className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
      style={cardStyle}
    >
      <SectionTitle
        icon={Icon}
        title={t('servicesSection.title')} /* CHANGED: "Services" */
        colors={mergedColors}
      />
      <ul className="lp-space-y-4">
        {services.map((service, index) => (
          <li
            key={index}
            className="lp-flex lp-items-center lp-justify-between lp-gap-4 group lp-transition-all lp-duration-200 hover:lp-translate-x-1"
          >
            <span className="lp-flex lp-items-center lp-min-w-0 lp-gap-3">
              <div className="lp-relative lp-w-12 lp-h-12 lp-rounded-full lp-overflow-hidden lp-border lp-transition-transform lp-duration-200 group-hover:lp-scale-110" style={imageBorderStyle}>
                <ImageWithPlaceholder
                  image={service.image}
                  alt={service.name}
                  className="lp-rounded-full"
                  placeholderColors={placeholderColors}
                />
                <div
                  className="lp-absolute lp-inset-0 lp-rounded-full lp-shadow-[0_0_10px_rgba(221,184,137,0.07)] group-hover:lp-shadow-[0_0_15px_rgba(221,184,137,0.1)] lp-transition-shadow lp-duration-200"
                />
              </div>

              <span
                className="lp-truncate lp-transition-colors lp-duration-200 lp-text-[#DDB889]/80 group-hover:lp-text-[#DDB889]"
                style={textStyle}
              >
                {service.name}
              </span>
            </span>
            <span
              className="lp-shrink-0 lp-transition-colors lp-duration-200 lp-text-[#DDB889]/80 group-hover:lp-text-[#DDB889]"
              style={textStyle}
            >
              {currency.symbol}{service.price.toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
